import { HttpInterceptorFn, HttpErrorResponse } from "@angular/common/http";
import { catchError, throwError } from "rxjs";
import { environment } from "../../../environments/environment";

/**
 * Função auxiliar para ler cookies
 * @param name Nome do cookie
 * @returns Valor do cookie ou null se não existir
 */
function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const cookiesArray = document.cookie.split(";");
  for (const cookieRaw of cookiesArray) {
    let cookie = cookieRaw;
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }
  return null;
}

/**
 * Função auxiliar para deletar cookies
 * @param name Nome do cookie a ser deletado
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Limpa todos os tokens de autenticação e redireciona para o app
 */
function handleUnauthorized(): void {
  // Limpa token do localStorage
  localStorage.removeItem("token");

  // Limpa cookies de autenticação
  deleteCookie("onboarding_token");

  // Limpa dados de sessão do onboarding
  try {
    sessionStorage.removeItem("yooga.lead");
  } catch {
    // Silently fail if sessionStorage is unavailable
  }

  // Redireciona para o app (ionic-yooga-app)
  window.location.href = environment.redirect_url;
}

/**
 * Interceptor HTTP que adiciona automaticamente:
 * - Token JWT do localStorage
 * - Headers padrão (Accept, Content-Type)
 * - Prefixo da API URL
 * - Tratamento de erro 401 (Unauthorized)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Primeiro tenta localStorage, depois cookies como fallback
  let token = localStorage.getItem("token");

  if (!token) {
    // Fallback para cookies
    token = getCookie("onboarding_token");
  }

  // Headers padrão
  let headers = req.headers
    .set("Accept", "application/json")
    .set("Content-Type", "application/json");

  // Adiciona token JWT se existir
  if (token) {
    headers = headers.set("Authorization", `Bearer ${token}`);
  }

  // Verifica se há override da URL da API
  const overrideApiUrl = req.headers.get("override-api-url");
  let url = req.url;

  if (overrideApiUrl) {
    // Remove o header de override e usa a URL customizada
    headers = headers.delete("override-api-url");
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = `${overrideApiUrl}${url}`;
    }
  } else if (!url.startsWith("http://") && !url.startsWith("https://")) {
    // URL padrão se não houver override
    url = `${environment.api_url}${url}`;
  }

  // Clona a requisição com os novos headers e URL
  const clonedRequest = req.clone({
    headers,
    url,
  });

  return next(clonedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Se receber 401 (Unauthorized), limpa tokens e redireciona para o app
      if (error.status === 401) {
        handleUnauthorized();
      }
      return throwError(() => error);
    })
  );
};
